import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// Record a single value OR bulk-paste up to 200 rows.
// Manual-only by design (server enforces).

const PERIODS = ["day", "week", "month", "quarter", "year", "custom"];

function todayISO() { return new Date().toISOString().slice(0, 10); }

function defaultRange(period) {
  const t = new Date();
  if (period === "day") return [todayISO(), todayISO()];
  if (period === "week") {
    const dow = (t.getDay() + 6) % 7; // Mon=0
    const start = new Date(t); start.setDate(t.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
  }
  if (period === "month") {
    const start = new Date(t.getFullYear(), t.getMonth(), 1);
    const end = new Date(t.getFullYear(), t.getMonth() + 1, 0);
    return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
  }
  if (period === "quarter") {
    const qm = Math.floor(t.getMonth() / 3) * 3;
    const start = new Date(t.getFullYear(), qm, 1);
    const end = new Date(t.getFullYear(), qm + 3, 0);
    return [start.toISOString().slice(0, 10), end.toISOString().slice(0, 10)];
  }
  if (period === "year") {
    return [`${t.getFullYear()}-01-01`, `${t.getFullYear()}-12-31`];
  }
  return [todayISO(), todayISO()];
}

export default function KPIValueDialog({ open, onOpenChange, kpi, editing, onSaved }) {
  const [tab, setTab] = useState("single");
  const [busy, setBusy] = useState(false);
  // single
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  // bulk
  const [bulkPeriod, setBulkPeriod] = useState("month");
  const [bulkText, setBulkText] = useState("");

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTab("single");
      setPeriod(editing.period || "month");
      setStart(editing.period_start || "");
      setEnd(editing.period_end || "");
      setValue(String(editing.value ?? ""));
      setNote(editing.note || "");
    } else {
      setTab("single");
      setPeriod("month");
      const [s, e] = defaultRange("month");
      setStart(s); setEnd(e);
      setValue(""); setNote("");
      setBulkPeriod("month"); setBulkText("");
    }
  }, [open, editing]);

  useEffect(() => {
    if (editing) return;
    const [s, e] = defaultRange(period);
    setStart(s); setEnd(e);
  }, [period, editing]);

  const submitSingle = async (e) => {
    e.preventDefault();
    if (!start || !end || value === "") return toast.error("Date range and value required");
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/kpis/${kpi.kpi_id}/values/${editing.value_id}`, {
          period, period_start: start, period_end: end,
          value: Number(value), note: note || null,
        });
        toast.success("Value updated");
      } else {
        await api.post(`/kpis/${kpi.kpi_id}/values`, {
          period, period_start: start, period_end: end,
          value: Number(value), note: note || null,
        });
        toast.success("Value recorded");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const submitBulk = async (e) => {
    e.preventDefault();
    const lines = bulkText.split("\n").map((l) => l.trim()).filter(Boolean);
    if (lines.length === 0) return toast.error("Paste some rows first");
    if (lines.length > 200) return toast.error("Max 200 rows per call");
    const rows = [];
    for (let i = 0; i < lines.length; i++) {
      const parts = lines[i].split(",").map((p) => p.trim());
      if (parts.length < 2) return toast.error(`Line ${i + 1}: need at least date,value`);
      const [d, v, ...rest] = parts;
      const noteText = rest.join(",").trim() || null;
      const num = Number(v);
      if (Number.isNaN(num)) return toast.error(`Line ${i + 1}: value not a number`);
      rows.push({ period_start: d, period_end: d, value: num, note: noteText });
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/kpis/${kpi.kpi_id}/values/bulk`,
                                         { period: bulkPeriod, rows });
      toast.success(`Inserted ${data.inserted} rows`);
      onSaved();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (!kpi) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" fullscreenOnMobile data-testid="kpi-value-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit value" : `Record value, ${kpi.name}`}</DialogTitle>
          <DialogDescription>
            Record a value for this period, or paste a batch of historical
            values to backfill the trend in one go.
          </DialogDescription>
        </DialogHeader>

        {editing ? (
          <SingleForm period={period} setPeriod={setPeriod}
            start={start} setStart={setStart} end={end} setEnd={setEnd}
            value={value} setValue={setValue} note={note} setNote={setNote}
            onSubmit={submitSingle} busy={busy} editing={editing} />
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="bg-[#F6F9FE] border border-slate-200 h-9 p-1 mb-4" data-testid="kpi-value-tabs">
              <TabsTrigger value="single" data-testid="kpi-value-tab-single"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Single</TabsTrigger>
              <TabsTrigger value="bulk" data-testid="kpi-value-tab-bulk"
                className="data-[state=active]:bg-white data-[state=active]:text-[#1D4ED8] rounded-md px-4">Bulk paste</TabsTrigger>
            </TabsList>
            <TabsContent value="single">
              <SingleForm period={period} setPeriod={setPeriod}
                start={start} setStart={setStart} end={end} setEnd={setEnd}
                value={value} setValue={setValue} note={note} setNote={setNote}
                onSubmit={submitSingle} busy={busy} />
            </TabsContent>
            <TabsContent value="bulk">
              <form onSubmit={submitBulk} className="space-y-3">
                <div>
                  <Label className="eyebrow">Period type</Label>
                  <Select value={bulkPeriod} onValueChange={setBulkPeriod}>
                    <SelectTrigger className="h-11 rounded-lg mt-2"
                      data-testid="kpi-bulk-period"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="eyebrow">Paste rows (one per line)</Label>
                  <textarea rows={8} value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    data-testid="kpi-bulk-text"
                    placeholder={"YYYY-MM-DD,value,note (optional)\n2026-01-31,4.2,Strong month\n2026-02-28,3.6"}
                    className="w-full font-mono text-sm rounded-lg border border-slate-200 p-2 mt-2" />
                  <p className="text-[11px] text-[#4B5773] mt-1">
                    Format: <code>YYYY-MM-DD,value</code> per line. Max 200 rows.
                  </p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy} data-testid="kpi-bulk-submit"
                    className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
                    {busy ? "Importing…" : "Import rows"}
                  </Button>
                </DialogFooter>
              </form>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SingleForm({ period, setPeriod, start, setStart, end, setEnd,
                       value, setValue, note, setNote, onSubmit, busy, editing }) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="eyebrow">Period</Label>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-11 rounded-lg mt-2"
              data-testid="kpi-single-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="eyebrow">Start</Label>
          <Input type="date" value={start} data-testid="kpi-single-start"
            onChange={(e) => setStart(e.target.value)}
            className="h-11 rounded-lg mt-2" />
        </div>
        <div>
          <Label className="eyebrow">End</Label>
          <Input type="date" value={end} data-testid="kpi-single-end"
            onChange={(e) => setEnd(e.target.value)}
            className="h-11 rounded-lg mt-2" />
        </div>
      </div>
      <div>
        <Label className="eyebrow">Value</Label>
        <Input type="number" step="any" value={value} required
          data-testid="kpi-single-value"
          onChange={(e) => setValue(e.target.value)}
          className="h-11 rounded-lg mt-2" />
      </div>
      <div>
        <Label className="eyebrow">Note (optional)</Label>
        <Input value={note} data-testid="kpi-single-note"
          onChange={(e) => setNote(e.target.value)}
          className="h-11 rounded-lg mt-2" />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={busy} data-testid="kpi-single-submit"
          className="rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
          {busy ? "Saving…" : editing ? "Save changes" : "Record value"}
        </Button>
      </DialogFooter>
    </form>
  );
}
